/**
 * The craft glyphs: one line-drawn emblem per registry slug, rendered white on
 * the worker's gradient by `WorkerMark`. Each glyph is the craft's tool, keyed
 * to the surname's meaning ("a wright is a maker", "fletchers made arrows fly
 * true") — the Option A "craft mark" sheet from the avatar style exploration,
 * ported at its original 72-unit geometry so the marks match the approved
 * mock-ups stroke for stroke. Each glyph carries its own stroke weight; the
 * shared cap/join/color come from the `WorkerMark` svg.
 *
 * Inline vectors, never files: the mark's contract is that a worker renders
 * identically on every surface without a fetch, so the glyphs ship in the
 * bundle as path data. An unknown slug returns null and the mark falls back to
 * the initial, which is what keeps a future worker renderable before this map
 * learns its craft.
 */
import type { ReactNode } from "react";

const GLYPHS: Record<string, ReactNode> = {
  // Builder: the hammer.
  wright: (
    <g strokeWidth={5.5}>
      <path d="M30 30 L52 52" />
      <path d="M22 38 L38 22" strokeWidth={14} />
    </g>
  ),
  // Reviewer: the inspection lens.
  proctor: (
    <g strokeWidth={5.5}>
      <circle cx="34" cy="34" r="12" />
      <path d="M43 43 L54 54" />
    </g>
  ),
  // Product Manager: the key — a bailey held the keys to the keep.
  bailey: (
    <g strokeWidth={5.5}>
      <circle cx="28" cy="28" r="9" />
      <path d="M34 34 L52 52 M46 46 L52 40 M40 40 L46 34" />
    </g>
  ),
  // Chronicler: the harp.
  harper: (
    <g strokeWidth={4.5}>
      <path d="M22 16 Q20 42 36 46 Q52 42 50 16" />
      <path d="M29 24 L29 43 M36 26 L36 45 M43 24 L43 43" strokeWidth={2.5} />
      <path d="M36 46 L36 54 M27 55 L45 55" />
    </g>
  ),
  // Staff Engineer: the senior officer's chevrons.
  reeve: (
    <g strokeWidth={5.5}>
      <path d="M22 30 L36 20 L50 30" />
      <path d="M22 42 L36 32 L50 42" />
      <path d="M22 54 L36 44 L50 54" />
    </g>
  ),
  // Security: the boundary shield.
  hayward: (
    <g strokeWidth={5}>
      <path d="M36 16 L54 23 Q54 45 36 56 Q18 45 18 23 Z" />
      <path d="M36 26 L36 40" />
      <circle cx="36" cy="45" r="1.5" fill="currentColor" stroke="none" />
    </g>
  ),
  // Triage: the parcel, carried to the right place.
  porter: (
    <g strokeWidth={4.5}>
      <path d="M20 28 L36 20 L52 28 L52 46 L36 54 L20 46 Z" />
      <path d="M20 28 L36 36 L52 28 M36 36 L36 54" />
    </g>
  ),
  // QA: the fletched arrow, flying true.
  fletcher: (
    <g strokeWidth={5}>
      <path d="M22 50 L50 22" />
      <path d="M38 22 L50 22 L50 34" />
      <path d="M22 42 L22 50 L30 50" />
    </g>
  ),
};

/**
 * The same marks, keyed by the registry's glyph identifier (YAG-591 §2). The
 * wire now names the glyph on the avatar (`avatar.glyph`) instead of leaving
 * every surface to infer it from the slug, so a workspace that renames a worker
 * — or a craft that arrives after this file was written — still draws its
 * craft's mark. The drawings are shared with the slug map above: one geometry
 * per craft, two ways in.
 */
const GLYPHS_BY_ID: Record<string, ReactNode> = {
  hammer: GLYPHS.wright,
  magnifier: GLYPHS.proctor,
  list: GLYPHS.bailey,
  quill: GLYPHS.harper,
  shield: GLYPHS.reeve,
  arrow: GLYPHS.fletcher,
};

/** The craft glyph for a slug, or null when the map doesn't know the craft. */
export function workerGlyph(slug: string): ReactNode | null {
  return GLYPHS[slug] ?? null;
}

/** The craft glyph the wire named, or null when it named none we can draw. */
export function workerGlyphById(glyph: string | undefined): ReactNode | null {
  return glyph ? (GLYPHS_BY_ID[glyph] ?? null) : null;
}
